import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';

// Analitik Plan 4 (spec §6.2–6.4) — bulgular (yalnız İK; Faz 1 kuralları yönetici kapsamı üretmez).
//   GET   v1/performance-insights?periodId[eq]=&status[in]=&ruleCode[eq]=&severity[eq]=&scopeType[eq]=&pageNumber=&pageSize=
//   GET   v1/performance-insights/{id}
//   PATCH v1/performance-insights/{id}/status {status, reason?}
// Metinler parametreli çeviri anahtarlarıdır: performance.insight.<kural kodu>.title|body (Faz 1'de LLM yok).

// P7a (analiz §26) — kalan AI kuralları: AI02 hedef düşüşü, AI03 büyük değerlendirme farkı, AI04 puanlama sapması (yalnız İK),
// AI06 süren yetkinlik açığı.
export const INSIGHT_RULES = [
  'pm.overdue.concentration', 'pm.stage.slowdown', 'pm.due.pileup', 'pm.potential.missing', 'pm.succession.gap', 'pm.review.attention',
  'pm.goal.decline', 'pm.review.gap', 'pm.rating.deviation', 'pm.competency.persistent_gap', 'data.completeness',
];
export const INSIGHT_SCOPES = ['tenant', 'department', 'manager', 'employee', 'rule'];
export const INSIGHT_STATUSES = ['new', 'reviewing', 'actioned', 'closed', 'invalid', 'expired'];
export const SEVERITIES = ['red', 'amber'];

// Kural → aksiyon açılırken önerilen tür (spec §6.3 tablosu).
export const SUGGESTED_ACTION_TYPE = {
  'pm.overdue.concentration': 'reminder',
  'pm.stage.slowdown': 'custom',
  'pm.due.pileup': 'reminder',
  'pm.potential.missing': 'potential_assessment',
  'pm.succession.gap': 'succession_review',
  'pm.review.attention': 'custom',
  'pm.goal.decline': 'goal_review',
  'pm.review.gap': 'expectation_meeting',
  'pm.rating.deviation': 'calibration_candidate',
  'pm.competency.persistent_gap': 'development_plan',
  'data.completeness': 'custom',
};

export const INSIGHT_STATUS_COLORS = {
  new: { color: '#0291EF', bg: '#EFF6FF' },
  reviewing: { color: '#D97706', bg: '#FFFBEB' },
  actioned: { color: '#7C3AED', bg: '#F5F3FF' },
  closed: { color: '#16A34A', bg: '#DCFCE7' },
  invalid: { color: '#6B7280', bg: '#F3F4F6' },
  expired: { color: '#6B7280', bg: '#F3F4F6' },
};

export const SEVERITY_COLORS = {
  red: { color: '#DC2626', bg: '#FEF2F2' },
  amber: { color: '#D97706', bg: '#FFFBEB' },
};

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

const pct = (v) => (v == null ? '-' : `${Math.round(Number(v) * 100)}`);

// Kapsam etiketi: kiracı kapsamında "Şirket", diğerlerinde kapsamın adı (yöneticide kişi kümesi dışı adlar sunucudan gelmez).
const scopeLabel = (insight, t) => (insight.scopeType === 'tenant'
  ? t('performance.insight.scope.tenant')
  : (insight.names?.[insight.scopeId] || '-'));

/** Kanıttan metin parametreleri (yalnız izinli kanıt alanları; adlar sunucunun `names` sözlüğünden). */
export function insightParams(insight, t) {
  const ev = insight?.evidence || {};
  const names = insight?.names || {};
  const th = ev.thresholds || {};
  switch (insight?.ruleCode) {
    case 'pm.overdue.concentration':
      return {
        overdueTasks: ev.overdueTasks,
        share: pct(ev.topAssigneeShare),
        topAssigneeCount: ev.topAssigneeCount,
        assigneePopulation: ev.assigneePopulation,
        scope: insight.scopeType === 'department' ? (names[insight.scopeId] || '-') : t('performance.insight.scope.tenant'),
      };
    case 'pm.stage.slowdown':
      return {
        stages: (ev.items || []).map((i) => t(`performance.insight.stage.${i.stageKey}`, { defaultValue: i.stageKey })).join(', '),
        windowDays: th.slowdownWindowDays,
        ratioPct: th.slowdownRatio != null ? Math.round((Number(th.slowdownRatio) - 1) * 100) : '-',
      };
    case 'pm.due.pileup':
      return {
        upcomingTasks: ev.upcomingTasks,
        untouchedTasks: ev.untouchedTasks,
        nearestDueInDays: ev.nearestDueInDays ?? '-',
        scope: names[insight.scopeId] || '-',
      };
    case 'pm.potential.missing':
      return { highBand: ev.highBand, missing: ev.missing };
    case 'pm.succession.gap':
      return { gapPositions: ev.gapPositions };
    case 'pm.review.attention':
      return {
        employee: names[insight.scopeId] || '-',
        reasons: (ev.reasons || []).map((r) => t(`performance.insight.reason.${r}`, { defaultValue: r })).join(', '),
      };
    case 'pm.goal.decline':
      return {
        scope: scopeLabel(insight, t),
        before: ev.avgBefore,
        after: ev.avgAfter,
        m46: ev.M46,
        n: ev.cohortN,
      };
    case 'pm.review.gap':
      return {
        scope: names[insight.scopeId] || '-',
        bigGap: ev.bigGap,
        matched: ev.matched,
        m18: ev.M18,
        threshold: th.reviewGapT,
      };
    case 'pm.rating.deviation':
      return {
        scope: names[insight.scopeId] || '-',
        m22: ev.M22,
        teamN: ev.teamN,
        peerN: ev.peerN,
      };
    case 'pm.competency.persistent_gap':
      return {
        scope: scopeLabel(insight, t),
        persons: ev.persons,
        pairs: ev.pairs,
        periods: ev.periods,
      };
    case 'data.completeness':
      return {
        rule: t(`performance.insight.${ev.sourceRule}.title`, { defaultValue: ev.sourceRule }),
        n: ev.n,
        required: ev.required,
      };
    default:
      return {};
  }
}

export const insightTitle = (insight, t) => t(`performance.insight.${insight.ruleCode}.title`, { defaultValue: insight.ruleCode });
export const insightBody = (insight, t) => t(`performance.insight.${insight.ruleCode}.body`, { ...insightParams(insight, t), defaultValue: '' });

const selectFilter = (t, labelKey, options, labelOf) => ({
  display: (fl, onChange, i, col) => (
    <FormControl variant="standard" fullWidth>
      <InputLabel>{t(labelKey)}</InputLabel>
      <Select
        value={fl[i][0] || ''}
        onChange={(e) => {
          fl[i] = e.target.value ? [e.target.value] : [];
          onChange(fl[i], i, col);
        }}
      >
        <MenuItem value=""><em>-</em></MenuItem>
        {options.map((o) => <MenuItem key={o} value={o}>{labelOf(o)}</MenuItem>)}
      </Select>
    </FormControl>
  ),
});

// P7a — E15 merkezi aynı tabloyu kendi süzgeç ucuyla (v1/performance-ai-center/findings) kullanır.
const usePerformanceInsightsApi = (queryParams, { endpoint = '/v1/performance-insights' } = {}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    api.get('/v1/evaluation-periods', { params: { pageSize: 200 } }).then((res) => {
      if (!res?.data?.status) return;
      const r = res.data.data;
      setPeriods((Array.isArray(r) ? r : (r?.data || [])).map((p) => ({ id: p.id, name: p.name })));
    });
  }, []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0 ? queryParams : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`${endpoint}?${new URLSearchParams(params)}`)
      .then((res) => {
        if (!res?.data?.status) { setData([]); return; }
        const result = res.data.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger, endpoint]);

  const changeStatus = useCallback(async (id, status, reason) => {
    try {
      await api.patch(`/v1/performance-insights/${id}/status`, { status, reason });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'severity',
      label: t('performance.insight.severity'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v) => {
          const cfg = SEVERITY_COLORS[v] || SEVERITY_COLORS.amber;
          return <Badge color={cfg.color} bg={cfg.bg}>{t(`performance.insight.severityValue.${v}`, { defaultValue: v })}</Badge>;
        },
        filterOptions: { defaultOp: 'eq', ...selectFilter(t, 'performance.insight.severity', SEVERITIES, (o) => t(`performance.insight.severityValue.${o}`)) },
      },
    },
    {
      name: 'ruleCode',
      label: t('performance.insight.rule'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v) => t(`performance.insight.${v}.title`, { defaultValue: v }),
        filterOptions: { defaultOp: 'eq', ...selectFilter(t, 'performance.insight.rule', INSIGHT_RULES, (o) => t(`performance.insight.${o}.title`)) },
      },
    },
    {
      name: 'scopeType',
      label: t('performance.insight.scopeLabel'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const scopeName = row?.names?.[row?.scopeId];
          const label = t(`performance.insight.scope.${v}`, { defaultValue: v });
          return scopeName ? `${label}: ${scopeName}` : label;
        },
        filterOptions: { defaultOp: 'eq', ...selectFilter(t, 'performance.insight.scopeLabel', INSIGHT_SCOPES, (o) => t(`performance.insight.scope.${o}`)) },
      },
    },
    {
      // Süzgeç dönem kimliğiyle (periodId[eq]); hücre dönem adını gösterir.
      name: 'periodId',
      label: t('performance.insight.period'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v, tableMeta) => data[tableMeta.rowIndex]?.periodName || '-',
        filterOptions: {
          defaultOp: 'eq',
          ...selectFilter(t, 'performance.insight.period', periods.map((p) => p.id), (id) => periods.find((p) => p.id === id)?.name || id),
        },
      },
    },
    {
      name: 'status',
      label: t('performance.insight.status'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v, tableMeta) => {
          const cfg = INSIGHT_STATUS_COLORS[v] || INSIGHT_STATUS_COLORS.new;
          const row = data[tableMeta.rowIndex];
          return (
            <>
              <Badge color={cfg.color} bg={cfg.bg}>{t(`performance.insight.statusValue.${v}`, { defaultValue: v })}</Badge>
              {row?.evidenceChangedSinceReview ? <> <Badge color="#D97706" bg="#FFFBEB">{t('performance.insight.staleBadge')}</Badge></> : null}
            </>
          );
        },
        filterOptions: { defaultOp: 'in', ...selectFilter(t, 'performance.insight.status', INSIGHT_STATUSES, (o) => t(`performance.insight.statusValue.${o}`)) },
      },
    },
    { name: 'n', label: t('performance.insight.n'), options: { filter: false, sort: false } },
    { name: 'actionCount', label: t('performance.insight.actionCount'), options: { filter: false, sort: false } },
    {
      name: 'detectedAt',
      label: t('performance.insight.detectedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDate(v, lang) : '-') },
    },
  ], [t, lang, data, periods]);

  return { fetching, data, pagination, columns, changeStatus, refresh };
};

export default usePerformanceInsightsApi;
